import axios from 'axios';
import { applyTenantHeaders } from '../../../services/tenantHeaders';
import { saveReturnLocation, setLogoutReason } from '../../../services/sessionManager';

export const API_DOMAIN = process.env.REACT_APP_API_DOMAIN || 'https://crmapp.katibu.africa/';

export const MAIN_APP_FRONTEND_DOMAIN = process.env.REACT_APP_FRONTEND_DOMAIN || 'http://crm-ui.local:8090/';

// Returns a fresh headers object on every call — reads localStorage each time.
export const getHeaders = () => {
    const token = localStorage.getItem('customer_management_token');
    const tenantId = localStorage.getItem('crm_tenant_id');
    const tenantToken = localStorage.getItem('crm_tenant_token');
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Token ${token}` } : {}),
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
        ...(tenantToken ? { 'X-Tenant-Token': tenantToken } : {}),
    };
};

// Plain object export kept for backward compat. Components that pass
// `{ headers }` as an axios config option will get a snapshot at call
// time — which is fine because getHeaders() is called fresh each render.
// The global interceptor below is the authoritative source for all requests.
export const headers = getHeaders();

// ── Global axios interceptor ────────────────────────────────────────────────
// Injects auth token + tenant credentials into EVERY axios request,
// including raw `axios.get/post` calls in individual components.
// This means no component file needs to be updated.
axios.interceptors.request.use((config) => {
    const token = localStorage.getItem('customer_management_token');
    if (token) {
        config.headers['Authorization'] = `Token ${token}`;
    }
    return applyTenantHeaders(config);
});

// Redirect to login on any 401 — catches cases where the token expires.
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const token = localStorage.getItem('customer_management_token');
            // Only redirect if we actually had a token (avoid redirect loop on login page)
            if (token) {
                saveReturnLocation();          // persist where the user was
                setLogoutReason('session_expired');
                localStorage.removeItem('customer_management_token');
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

export const API_ROUTES = {
  // Auth
  verifyHandshake: 'security/audit/verify-handshake/',

  // Central
  users: 'lead/users/',

  // Lead Management
  contact: 'lead/contacts/',
  lead: 'lead/leads/',
  task: 'lead/tasks/',
  opportunity: 'lead/opportunities/',
  workflow: 'lead/workflows/',
  emailTemplate: 'lead/email-templates/',
  salesTeam: 'lead/sales-team/',
  salesRegion: 'lead/sales-region/',
  salesTarget: 'lead/sales-target/',
  salesPerformance: 'lead/sales-performance/',

  // Customer Management
  customer: 'customer/customers/',
  customerInteraction: 'customer/interactions/',
  customerNote: 'customer/notes/',
  customerActivity: 'customer/activities/',
  customerActivityType: 'customer/activity-types/',
  customerCommunicationLog: 'customer/communication-logs/',
  customerDocument: 'customer/documents/',
  customerReminder: 'customer/reminders/',
  salesStage: 'customer/sales-stages/',
  salesStatus: 'customer/sales-statuses/',
  salesForecast: 'customer/sales-forecasts/',
  webhookConfig: 'customer/webhook-configs/',
  asyncEventLog: 'customer/async-event-logs/',
  gatewayMessageLog: 'customer/gateway-message-logs/',
  customerSyncLog: 'customer/customer-sync-logs/',
  customerChangeLog: 'customer/customer-change-logs/',

  // Opportunity Management
  opportunityStage: 'opportunity/opportunity-stages/',
  opportunityActivity: 'opportunity/opportunity-activities/',
  opportunityNotes: 'opportunity/opportunity-notes/',
  opportunityDocument: 'opportunity/opportunity-documents/',
  opportunityAssignedUser: 'opportunity/opportunity-assigned-users/',
  opportunityHistory: 'opportunity/opportunity-history/',
  opportunityNotification: 'opportunity/opportunity-notifications/',
  notifications: 'opportunity/notifications/',
  opportunityFollowUp: 'opportunity/opportunity-follow-ups/',
  opportunitySource: 'opportunity/opportunity-sources/',
  opportunityPriority: 'opportunity/opportunity-priorities/',
  opportunityStatus: 'opportunity/opportunity-statuses/',
  opportunityType: 'opportunity/opportunity-types/',
  opportunityCategory: 'opportunity/opportunity-categories/',
  opportunitySubCategory: 'opportunity/opportunity-subcategories/',
  opportunityTag: 'opportunity/opportunity-tags/',
  opportunityRating: 'opportunity/opportunity-ratings/',
  opportunityComment: 'opportunity/opportunity-comments/',
  opportunityTask: 'opportunity/opportunity-tasks/',
  opportunityEvent: 'opportunity/opportunity-events/',
  opportunityMeeting: 'opportunity/opportunity-meetings/',
  opportunityCall: 'opportunity/opportunity-calls/',

  // Quote Management
  quote: 'quote/quotes/',
  quoteStatuses: 'quote/statuses/',
  quoteStatus: 'quote/statuses/',
  products: 'quote/products/',
  quoteProduct: 'quote/products/',
  lineItems: 'quote/line/items/',
  quoteLineItem: 'quote/line/items/',
  quoteComments: 'quote/comments/',
  quoteVersion: 'quote/versions/',
  quoteDocument: 'quote/documents/',
  quoteTask: 'quote/tasks/',
  quoteFollowUp: 'quote/follow-ups/',
  quotePayment: 'quote/payments/',
  quoteApproval: 'quote/approvals/',
  archivedQuote: 'quote/archived/',

  // Reporting & Analytics
  report: 'reporting/reports/',
  reportField: 'reporting/report/fields/',
  reportFilter: 'reporting/filters/',
  scheduledReport: 'reporting/scheduled/reports/',
  reportData: 'reporting/report/data/',
  segmentation: 'reporting/segmentations/',
  analyticsData: 'reporting/analytics/data/',
  trendAnalysis: 'reporting/trend/analysis/',
  forecast: 'reporting/forecasts/',
  reportUserAccess: 'reporting/user/access/control/',
  reportFeedback: 'reporting/report/feedback/',
  savedWidget: 'reporting/widgets/',

  // Security & Audit
  auditLog: 'security/audit/audit-log/',
  loginActivity: 'security/audit/login-activity/',
  accessControl: 'security/audit/access-control/',
  dataIntegrityCheck: 'security/audit/data-integrity-check/',
  securityAlert: 'security/audit/security-alert/',

  // Administrator (superadmin only)
  tenantCredentials: 'security/audit/tenant-credentials/',
  securityUsers: 'security/audit/users/',
};

export const handleError = (error) => {
    console.error('Error caught in handleError:', error);

    if (error.response) {
      const status = error.response.status;

      switch (status) {
        case 400:
          console.warn('Bad Request:', error.response.data);
          window.location.href = '/400';
          break;

        case 401:
          console.warn('Unauthorized: Redirecting to login');
          window.location.href = '/401';
          break;

        case 403:
          console.warn('Forbidden: Access denied');
          window.location.href = '/403';
          break;

        case 404:
          console.warn('Not Found');
          window.location.href = '/404';
          break;

        case 500:
        default:
          console.error('Server error');
          window.location.href = '/500';
          break;
      }
    } else if (error.request) {
      console.error('Network error or no response from server.');
      window.location.href = '/500';
    } else {
      console.error('Unexpected error:', error.message);
      window.location.href = '/500';
    }
  };

export const getApiEndpoint = (route) => `${API_DOMAIN}${API_ROUTES[route]}`;
